/**
 * DataBest — Caregiver Performance List
 * Supervisor+ / Analyst: table of caregivers with core KPIs.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
    Search,
    ChevronRight,
    Activity,
    Loader2,
    RefreshCw,
} from "lucide-react";
import { caregiverPerfApi } from "../api/caregiverPerfApi";
import PerfPeriodSelector, {
    buildDefaultPeriod,
} from "../components/PerfPeriodSelector";

const grade_color = (g) => {
    if (!g) return "bg-slate-100 text-slate-500";
    if (["A"].includes(g)) return "bg-brand-green-100 text-brand-green-800";
    if (["B", "C"].includes(g)) return "bg-brand-blue-100 text-brand-blue-800";
    return "bg-amber-100 text-amber-800";
};

const score_color = (v) => {
    if (v === null || v === undefined) return "bg-slate-100 text-slate-500";
    if (v >= 85) return "bg-brand-green-100 text-brand-green-800";
    if (v >= 70) return "bg-brand-blue-100 text-brand-blue-800";
    return "bg-amber-100 text-amber-800";
};

const CaregiverPerfListPage = () => {
    const navigate = useNavigate();
    const [period, setPeriod] = useState(buildDefaultPeriod(30));
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("caregiver_score");
    const [sortDir, setSortDir] = useState("desc");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await caregiverPerfApi.list(period);
            setItems(data.items || []);
        } catch (err) {
            toast.error(err?.message || "Could not load performance data");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const sorted = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? items.filter(
                  (i) =>
                      i.caregiver_name.toLowerCase().includes(q) ||
                      (i.caregiver_email || "").toLowerCase().includes(q),
              )
            : items;
        const dir = sortDir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const va = a[sortBy] ?? 0;
            const vb = b[sortBy] ?? 0;
            if (va === vb) return a.caregiver_name.localeCompare(b.caregiver_name);
            return va > vb ? dir : -dir;
        });
    }, [items, query, sortBy, sortDir]);

    const clickSort = (col) => {
        if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortBy(col);
            setSortDir("desc");
        }
    };

    const arrow = (col) => (sortBy === col ? (sortDir === "asc" ? " ↑" : " ↓") : "");

    return (
        <div className="mx-auto max-w-6xl" data-testid="caregiver-perf-list">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
            >
                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                            Plans & Reports
                        </p>
                        <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                            Caregiver Performance
                        </h1>
                        <p className="mt-1 text-sm text-slate-500">
                            KPIs calculated only within each caregiver&apos;s
                            assignment windows.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        data-testid="caregiver-perf-refresh"
                    >
                        <RefreshCw
                            className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </button>
                </div>

                {/* Filters bar */}
                <div className="mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white/85 p-4 backdrop-blur">
                    <div className="flex flex-wrap items-center gap-3">
                        <PerfPeriodSelector value={period} onChange={setPeriod} />
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
                            <input
                                type="search"
                                placeholder="Search caregiver…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="caregiver-perf-search"
                                className="h-8 w-56 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                            />
                        </div>
                        <span
                            className="ml-auto rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-600"
                            data-testid="caregiver-perf-total"
                        >
                            {sorted.length} caregivers
                        </span>
                    </div>
                </div>

                {/* Table (desktop) / Cards (mobile) */}
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center p-12 text-slate-500">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Calculating KPIs…
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="p-12 text-center" data-testid="caregiver-perf-empty">
                            <Activity className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                            <p className="text-sm font-semibold text-slate-500">
                                No caregivers to show in this period.
                            </p>
                        </div>
                    ) : (
                    <>
                        {/* Mobile cards */}
                        <ul className="divide-y divide-slate-100 md:hidden" data-testid="caregiver-perf-cards">
                            {sorted.map((r) => (
                                <li
                                    key={r.caregiver_id}
                                    onClick={() =>
                                        navigate(
                                            `/reports/caregiver-performance/${r.caregiver_id}?from=${period.period_start}&to=${period.period_end}`,
                                        )
                                    }
                                    data-testid={`caregiver-perf-card-${r.caregiver_id}`}
                                    className="cursor-pointer p-4 hover:bg-slate-50 active:bg-slate-100"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-semibold text-slate-900">
                                                {r.caregiver_name}
                                            </p>
                                            <p className="truncate text-[11px] text-slate-400">
                                                {r.caregiver_email}
                                            </p>
                                            {r.houses?.length ? (
                                                <p className="mt-0.5 truncate text-[11px] text-slate-500">
                                                    {r.houses.join(", ")}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="flex flex-shrink-0 items-center gap-1.5">
                                            {r.caregiver_score != null ? (
                                                <span
                                                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${score_color(r.caregiver_score)}`}
                                                >
                                                    Score {r.caregiver_score}%
                                                </span>
                                            ) : null}
                                            <ChevronRight className="h-4 w-4 text-slate-300" />
                                        </div>
                                    </div>

                                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                                        <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                                            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                                Total Rec.
                                            </p>
                                            <p className="text-sm font-bold tabular-nums text-slate-900">
                                                {r.total_records ?? 0}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                                            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                                % Compl.
                                            </p>
                                            <p className="text-sm font-bold tabular-nums text-slate-900">
                                                {r.pct_records_completed != null ? `${r.pct_records_completed}%` : "—"}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-slate-50 px-2 py-1.5">
                                            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                                On-Time
                                            </p>
                                            <p className="text-sm font-bold tabular-nums text-slate-900">
                                                {r.on_time_rate}%
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                                        <span
                                            className={`rounded-full px-2 py-0.5 font-semibold ${
                                                r.coverage_gap_pct > 30
                                                    ? "bg-red-100 text-red-700"
                                                    : r.coverage_gap_pct > 10
                                                      ? "bg-amber-100 text-amber-700"
                                                      : "bg-slate-100 text-slate-600"
                                            }`}
                                        >
                                            Gap {r.coverage_gap_pct}%
                                        </span>
                                        {(r.report_quality_pct ?? r.avg_overall) != null && (
                                            <span className={`rounded-full px-2 py-0.5 font-semibold ${grade_color(r.letter_grade)}`}>
                                                Quality {r.report_quality_pct ?? r.avg_overall}%
                                            </span>
                                        )}
                                        {r.flags_count > 0 && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-700">
                                                {r.red_flags_count > 0 && (
                                                    <span className="rounded-full bg-red-500/90 px-1.5 text-white">
                                                        {r.red_flags_count}
                                                    </span>
                                                )}
                                                Flags {r.flags_count}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Desktop table */}
                        <table className="hidden min-w-full divide-y divide-slate-100 text-sm md:table">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                <tr>
                                    <th className="cursor-pointer px-3 py-2 text-left hover:text-slate-800" onClick={() => clickSort("caregiver_name")}>
                                        Caregiver{arrow("caregiver_name")}
                                    </th>
                                    <th className="px-3 py-2 text-left">Houses</th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("individuals_covered")}>
                                        Individuals{arrow("individuals_covered")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("total_records")}>
                                        Total Records{arrow("total_records")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("pct_records_completed")}>
                                        % Records Completed{arrow("pct_records_completed")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("on_time_rate")}>
                                        On-Time %{arrow("on_time_rate")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("coverage_gap_pct")}>
                                        Coverage Gap %{arrow("coverage_gap_pct")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("avg_overall")}>
                                        Report Quality{arrow("avg_overall")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("caregiver_score")}>
                                        Caregiver Score{arrow("caregiver_score")}
                                    </th>
                                    <th className="cursor-pointer px-3 py-2 text-right hover:text-slate-800" onClick={() => clickSort("flags_count")}>
                                        Flags{arrow("flags_count")}
                                    </th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {sorted.map((r) => (
                                    <tr
                                        key={r.caregiver_id}
                                        onClick={() =>
                                            navigate(
                                                `/reports/caregiver-performance/${r.caregiver_id}?from=${period.period_start}&to=${period.period_end}`,
                                            )
                                        }
                                        data-testid={`caregiver-perf-row-${r.caregiver_id}`}
                                        className="cursor-pointer hover:bg-slate-50"
                                    >
                                        <td className="px-3 py-2 font-semibold text-slate-900">
                                            {r.caregiver_name}
                                            <div className="text-[10px] font-normal text-slate-400">
                                                {r.caregiver_email}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">
                                            {r.houses?.join(", ") || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {r.individuals_covered}
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold tabular-nums">
                                            {r.total_records ?? 0}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {r.pct_records_completed != null ? `${r.pct_records_completed}%` : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {r.on_time_rate}%
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            <span
                                                className={
                                                    r.coverage_gap_pct > 30
                                                        ? "text-red-700 font-bold"
                                                        : r.coverage_gap_pct > 10
                                                          ? "text-amber-700 font-semibold"
                                                          : "text-slate-600"
                                                }
                                            >
                                                {r.coverage_gap_pct}%
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {(r.report_quality_pct ?? r.avg_overall) !== null && (r.report_quality_pct ?? r.avg_overall) !== undefined ? (
                                                <div className="inline-flex items-center gap-1">
                                                    <span className="tabular-nums">
                                                        {r.report_quality_pct ?? r.avg_overall}%
                                                    </span>
                                                    <span
                                                        className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${grade_color(r.letter_grade)}`}
                                                    >
                                                        {r.letter_grade}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {r.caregiver_score != null ? (
                                                <span
                                                    className={`inline-block rounded-md px-2 py-0.5 text-xs font-bold tabular-nums ${score_color(r.caregiver_score)}`}
                                                >
                                                    {r.caregiver_score}%
                                                </span>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {r.flags_count > 0 ? (
                                                <div className="inline-flex items-center gap-1">
                                                    {r.red_flags_count > 0 && (
                                                        <span className="rounded-full bg-red-100 px-1.5 py-0 text-[10px] font-bold text-red-700">
                                                            {r.red_flags_count}
                                                        </span>
                                                    )}
                                                    <span className="rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-bold text-amber-700">
                                                        {r.flags_count}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <ChevronRight className="inline h-4 w-4 text-slate-300" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default CaregiverPerfListPage;
